<template>
  <div>
    <div class="toolbar">
      <el-input
        v-model="keyword"
        clearable
        placeholder="单号 / 工人 / 事项"
        style="width:200px"
        @keyup.enter.native="load"
      />
      <el-select v-model="projectId" clearable filterable placeholder="项目" style="width:200px" @change="load">
        <el-option v-for="p in projects" :key="p.id" :label="projectLabel(p)" :value="p.id" />
      </el-select>
      <el-select v-model="status" clearable placeholder="审批状态" style="width:130px" @change="load">
        <el-option v-for="o in laborReqStatusFilterOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>
      <el-select v-model="payStatus" clearable placeholder="发放状态" style="width:140px" @change="load">
        <el-option v-for="o in laborPayStatusFilterOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>
      <el-select v-model="confirmStatus" clearable placeholder="确认状态" style="width:140px" @change="load">
        <el-option v-for="o in laborConfirmStatusFilterOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="load">查询</el-button>
      <el-button icon="el-icon-download" :loading="exporting" :disabled="!list.length" @click="onExport">导出</el-button>
    </div>

    <el-table :data="pageList" border v-loading="loading" show-summary :summary-method="summaryMethod">
      <el-table-column prop="reqNo" label="申请单号" width="160" show-overflow-tooltip />
      <el-table-column prop="projectName" label="项目" min-width="140" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <project-name-link :project-id="row.projectId" :name="row.projectName" />
        </template>
      </el-table-column>
      <el-table-column prop="projectAddress" label="项目地址" min-width="160" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.projectAddress || '-' }}</template>
      </el-table-column>
      <el-table-column prop="stageName" label="工序" width="110" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.stageName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="applyTypeName" label="申请类型" width="100" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.applyTypeName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="workerName" label="工人" width="110" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.workerName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="itemName" label="事项" min-width="140" show-overflow-tooltip />
      <el-table-column prop="unit" label="单位" width="70" align="center">
        <template slot-scope="{ row }">{{ row.unit || '-' }}</template>
      </el-table-column>
      <el-table-column prop="qty" label="数量" width="90" align="right">
        <template slot-scope="{ row }">{{ fmtQty(row.qty) }}</template>
      </el-table-column>
      <el-table-column prop="price" label="单价" width="100" align="right">
        <template slot-scope="{ row }">{{ formatMoney(row.price) }}</template>
      </el-table-column>
      <el-table-column prop="amount" label="金额" width="110" align="right">
        <template slot-scope="{ row }">{{ formatMoney(row.amount) }}</template>
      </el-table-column>
      <el-table-column label="审批状态" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(laborReqStatusMap, row.status)">
            {{ statusLabel(laborReqStatusMap, row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="发放状态" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(laborPayStatusMap, row.payStatus || 'NONE')">
            {{ statusLabel(laborPayStatusMap, row.payStatus || 'NONE') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="确认状态" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(laborConfirmStatusMap, row.confirmStatus || 'NONE')">
            {{ statusLabel(laborConfirmStatusMap, row.confirmStatus || 'NONE') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createdByName" label="创建人" width="80" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="90">
        <template slot-scope="{ row }">{{ formatDateOnly(row.createdAt) }}</template>
      </el-table-column>
    </el-table>
    <list-pagination
      :total="listTotal"
      :page="page"
      :page-size="pageSize"
      @current-change="onPageChange"
      @size-change="onPageSizeChange"
    />
  </div>
</template>

<script>
import { listLaborReqLineRows, exportLaborReqLineRows } from '../../api/labor'
import { listProjects } from '../../api/project'
import {
  laborReqStatusMap, laborReqStatusFilterOptions,
  laborPayStatusMap, laborPayStatusFilterOptions,
  laborConfirmStatusMap, laborConfirmStatusFilterOptions,
  statusLabel, statusType
} from '../../utils/status'
import ListPagination from '../../components/ListPagination.vue'
import ProjectNameLink from '../../components/ProjectNameLink.vue'
import listPaging from '../../mixins/listPaging'

const SUM_PROPS = new Set(['qty', 'amount'])

export default {
  name: 'LaborReqLines',
  components: { ListPagination, ProjectNameLink },
  mixins: [listPaging],
  data() {
    return {
      laborReqStatusMap,
      laborReqStatusFilterOptions,
      laborPayStatusMap,
      laborPayStatusFilterOptions,
      laborConfirmStatusMap,
      laborConfirmStatusFilterOptions,
      keyword: '',
      projectId: null,
      status: '',
      payStatus: '',
      confirmStatus: '',
      projects: [],
      list: [],
      loading: false,
      exporting: false
    }
  },
  created() {
    this.loadFilters()
    this.load()
  },
  methods: {
    statusLabel,
    statusType,
    formatDateOnly(v) {
      if (!v) return '-'
      const s = String(v).replace('T', ' ')
      return s.length >= 10 ? s.slice(0, 10) : s
    },
    projectLabel(p) {
      if (!p) return ''
      return p.name || p.customerName || ('项目' + p.id)
    },
    num(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    fmtQty(v) {
      if (v == null || v === '') return '0'
      const n = this.num(v)
      if (Number.isInteger(n)) return String(n)
      return String(Math.round(n * 10000) / 10000)
    },
    formatMoney(v) {
      return this.num(v).toFixed(2)
    },
    summaryMethod({ columns }) {
      const sums = []
      const rows = this.list || []
      columns.forEach((col, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        const prop = col.property
        if (!SUM_PROPS.has(prop)) {
          sums[index] = ''
          return
        }
        let total = 0
        rows.forEach(row => { total += this.num(row[prop]) })
        sums[index] = prop === 'amount' ? this.formatMoney(total) : this.fmtQty(total)
      })
      return sums
    },
    async loadFilters() {
      const pr = await listProjects({})
      this.projects = pr.data || []
    },
    async load() {
      this.loading = true
      try {
        const res = await listLaborReqLineRows({
          keyword: this.keyword || undefined,
          projectId: this.projectId || undefined,
          status: this.status || undefined,
          payStatus: this.payStatus || undefined,
          confirmStatus: this.confirmStatus || undefined
        })
        this.list = res.data || []
        this.page = 1
      } finally {
        this.loading = false
      }
    },
    async onExport() {
      if (!this.list.length) {
        return this.$message.warning('暂无数据可导出')
      }
      this.exporting = true
      try {
        const res = await exportLaborReqLineRows({
          keyword: this.keyword || undefined,
          projectId: this.projectId || undefined,
          status: this.status || undefined,
          payStatus: this.payStatus || undefined,
          confirmStatus: this.confirmStatus || undefined
        })
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '工资明细.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar { display:flex; gap:8px; margin-bottom:12px; flex-wrap:wrap; }
</style>
